/**
 * Confirmación ANTES de mandar mensajes reales a clientes reales.
 *
 * Al montarse le pide al backend el número real con un dry-run (misma cola, mismos gates, cero
 * efectos) y recién ahí deja confirmar. Lo usan el centro de control («Reanudar envío») y el
 * wizard («Lanzar la campaña»): antes el wizard mandaba directo, sin número y sin freno.
 *
 * El componente NUNCA manda nada por su cuenta: confirmar sólo llama a `onConfirmar`. Quien lo usa
 * tiene que mandar con la MISMA selección que le pasa acá (`grupos`/`lineas`), o el número que el
 * operador aprobó no es el que sale.
 */
import { useEffect, useRef, useState } from "react";
import { X, Loader2, Send, CheckCircle2, ShieldCheck, Smartphone } from "lucide-react";
import { activaApi } from "../api";
import { num, plural } from "../utils/format";

interface Props {
  campaignId: string;
  /** grupos, en la convención de la API: undefined = todos. Nunca [] (del otro lado es "todos"). */
  grupos?: string[];
  /** líneas de WhatsApp, misma convención: undefined = todas las conectadas */
  lineas?: string[];
  titulo: string;
  onConfirmar: () => void;
  onCerrar: () => void;
  /** salida cuando no hay líneas: sin esto el operador queda frente a un botón gris sin camino */
  onConectarLinea?: () => void;
  /** reanudar: se reintentan los rebotes. En un primer envío esa viñeta no aplica y mentiría. */
  mostrarReintentos?: boolean;
}

const ERROR_GENERICO = "No pudimos calcular cuántos mensajes saldrían. No se envió nada.";

export function ConfirmarEnvio({ campaignId, grupos, lineas, titulo, onConfirmar, onCerrar, onConectarLinea, mostrarReintentos }: Props) {
  const [previo, setPrevio] = useState<any>(null);
  const [error, setError] = useState("");
  /**
   * El dry-run se pide UNA vez por apertura del modal. El ref evita repetirlo por el doble montaje
   * de StrictMode y por los arrays nuevos que el padre arma en cada render; la selección no cambia
   * mientras el modal está abierto (el que lo abre lo desmonta al cerrar).
   */
  const pedido = useRef(false);
  useEffect(() => {
    if (pedido.current) return;
    pedido.current = true;
    activaApi
      .sendDryRun(campaignId, grupos, lineas)
      .then((r: any) => {
        // `req()` no tira excepción con un 422: lo devuelve con success:false y el motivo del
        // backend (sin mensajes, dataset de ejemplo…). Ese motivo es lo que el operador tiene que
        // leer; "no pudimos calcular" lo dejaría sin saber qué arreglar.
        if (r?.success === false) setError(r.error || ERROR_GENERICO);
        else if (r?.data) setPrevio(r.data);
        else setError(ERROR_GENERICO);
      })
      // Si el dry-run falla sin respuesta, el modal no puede quedarse con el spinner para siempre.
      .catch(() => setError(ERROR_GENERICO));
  }, [campaignId, grupos, lineas]);

  // Fuera de la ventana horaria el envío no manda ninguno ni deja nada en espera (`dispatch` los saltea y
  // la campaña vuelve a «lista para enviar»): confirmar no haría nada. Antes el botón decía «Encolar N»
  // y el texto prometía que salían solos al abrir la ventana; nada los reanuda.
  const puedeConfirmar = !!previo && previo.encolados > 0 && !previo.faltanLineas && previo.enFranja !== false;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/40" onClick={onCerrar}>
      <div role="dialog" aria-modal="true" aria-labelledby="confirm-envio-title" className="bg-white rounded-2xl shadow-xl max-w-md w-full p-6" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between gap-3">
          <h2 id="confirm-envio-title" className="text-lg font-semibold text-slate-900">{titulo}</h2>
          <button onClick={onCerrar} className="text-slate-400 hover:text-slate-600" aria-label="Cerrar"><X size={18} /></button>
        </div>
        {/* el número REAL, calculado por el backend sin mandar nada (dry-run) */}
        {error ? (
          <div className="mt-4 rounded-xl border-2 border-red-300 bg-red-50 p-4">
            <p className="text-sm font-bold text-red-900">{error}</p>
            <p className="text-xs text-red-800 mt-1">
              {error === ERROR_GENERICO
                ? "Cerrá y probá de nuevo. Si sigue, avisale al equipo técnico."
                : "No se envió nada. Corregilo y volvé a intentar."}
            </p>
          </div>
        ) : !previo ? (
          <div className="mt-4 rounded-xl bg-slate-50 p-4 flex items-center gap-2.5 text-sm text-slate-500">
            <Loader2 size={15} className="animate-spin" /> Calculando cuántos saldrían…
          </div>
        ) : previo.encolados === 0 ? (
          <div className="mt-4 rounded-xl bg-slate-50 p-4">
            <p className="text-sm font-semibold text-slate-700">No hay nada para enviar</p>
            <p className="text-xs text-slate-500 mt-0.5">{previo.mensaje}</p>
          </div>
        ) : previo.enFranja === false || previo.faltanLineas ? (
          /* El backend YA calculó que no va a salir ninguno (ventana cerrada o sin líneas): pintar
             el bloque verde con el botón habilitado prometería un envío que no ocurre. */
          <div className="mt-4 rounded-xl border-2 border-amber-300 bg-amber-50 p-4">
            <p className="text-sm font-bold text-amber-900">
              {previo.lineasOriginalesDesconectadas
                ? "Las líneas del envío original no están conectadas"
                : previo.faltanLineas ? "No hay ninguna línea de WhatsApp conectada" : "Ahora no saldría ninguno"}
            </p>
            <p className="text-xs text-amber-800 mt-1 leading-relaxed">{previo.mensaje}</p>
            {previo.enFranja === false && !previo.faltanLineas && (
              <p className="text-xs text-amber-800 mt-1.5">
                No quedan en espera: {mostrarReintentos
                  ? <>cuando abra la ventana ({previo.franja}), volvé a tocar «Reanudar envío».</>
                  : <>lanzala cuando abra la ventana ({previo.franja}), o programala para esa hora.</>}
              </p>
            )}
            {previo.faltanLineas && onConectarLinea && (
              <button onClick={onConectarLinea} className="mt-2.5 inline-flex items-center gap-1.5 rounded-lg border border-amber-300 bg-white px-3 py-1.5 text-xs font-semibold text-amber-900 hover:bg-amber-100">
                <Smartphone size={13} /> Conectar una línea
              </button>
            )}
          </div>
        ) : (
          <div className="mt-4 rounded-xl border border-emerald-200 bg-emerald-50/60 p-4">
            <p className="text-[28px] leading-none font-semibold text-emerald-800">{num(previo.encolados)}</p>
            <p className="text-sm text-emerald-900 font-medium mt-1">mensajes reales a clientes reales</p>
            <p className="text-xs text-emerald-700 mt-1.5">
              {num(previo.porCanal?.WhatsApp || 0)} por WhatsApp ({previo.lineasWhatsApp} {plural(previo.lineasWhatsApp, "línea", "líneas")}) · {num(previo.porCanal?.Email || 0)} por email
              {previo.reintentos > 0 && <> · incluye {num(previo.reintentos)} {plural(previo.reintentos, "reintento", "reintentos")}</>}
            </p>
          </div>
        )}

        {/* H03: sin FRONTEND_URL el backend saca los mails de la cola (no pueden llevar link de
            baja). En las otras ramas ya lo dice `previo.mensaje`; en la verde el número sólo
            cuenta lo que sale, y el operador confirmaría sin saber que sus mails se quedan. Avisa,
            no bloquea: los WhatsApp salen igual y los mails esperan, sin gastar intentos.
            El motivo lo escribe el backend en palabras del dueño (antes nombraba «FRONTEND_URL» y el
            «servidor»), ya con su punto: acá sólo se le asegura uno, sin duplicarlo. */}
        {previo?.encolados > 0 && previo.enFranja !== false && !previo.faltanLineas && previo.emailsSinLinkBaja > 0 && (
          <div className="mt-3 rounded-xl border-2 border-amber-300 bg-amber-50 p-3">
            <p className="text-sm font-bold text-amber-900">
              {num(previo.emailsSinLinkBaja)} {plural(previo.emailsSinLinkBaja, "mail no va a salir", "mails no van a salir")}
            </p>
            <p className="text-xs text-amber-800 mt-1 leading-relaxed">
              {String(previo.motivoEmail || "").trim().replace(/\.?$/, ".")} Los WhatsApp salen igual; los mails quedan pendientes y salen en el próximo envío, cuando esté configurado.
            </p>
          </div>
        )}

        {/* Reanudar mantiene el alcance del primer envío (el backend lo aplica): hay que decirlo,
            porque el operador puede creer que "reanudar" es mandarle a toda la base. */}
        {previo?.alcanceOriginal && (
          <p className="text-xs text-slate-600 bg-slate-50 rounded-lg px-3 py-2 mt-3">
            Va sólo {plural(previo.gruposOriginales, "al grupo", `a los ${num(previo.gruposOriginales || 0)} grupos`)} del envío original.
            {previo.fueraDeSeleccion > 0 && <> {num(previo.fueraDeSeleccion)} {plural(previo.fueraDeSeleccion, "persona de otros grupos queda afuera", "personas de otros grupos quedan afuera")}.</>}
          </p>
        )}

        {/* Cada <li> es flex (ícono + texto): el texto va entero en UN <span>. Suelto, cada pedazo
            entre <strong> era un ítem flex aparte y la frase se partía en columnas («Se respetan
            siempre las | bajas | y el cupo diario…»). El número va pegado a lo que cuenta:
            `bloqueadosDnc` son sólo bajas, y al lado de «cupo diario» se leía como si fuera el cupo. */}
        <ul className="text-sm text-slate-600 mt-3.5 space-y-1.5">
          <li className="flex items-start gap-2">
            <CheckCircle2 size={14} className="text-emerald-500 flex-shrink-0 mt-0.5" />
            <span>
              A quien <strong>ya recibió</strong> el mensaje no se le vuelve a escribir
              {previo?.yaEntregados > 0 && <> ({num(previo.yaEntregados)} {plural(previo.yaEntregados, "queda afuera", "quedan afuera")})</>}.
            </span>
          </li>
          {mostrarReintentos && (
            <li className="flex items-start gap-2">
              <CheckCircle2 size={14} className="text-emerald-500 flex-shrink-0 mt-0.5" />
              <span>Los que <strong>rebotaron</strong> se reintentan (hasta 3 intentos).</span>
            </li>
          )}
          <li className="flex items-start gap-2">
            <CheckCircle2 size={14} className="text-emerald-500 flex-shrink-0 mt-0.5" />
            <span>
              Se respetan siempre las <strong>bajas</strong>
              {previo?.bloqueadosDnc > 0 && <> ({num(previo.bloqueadosDnc)} {plural(previo.bloqueadosDnc, "persona pidió la baja y queda afuera", "personas pidieron la baja y quedan afuera")})</>}
              {" "}y el cupo diario.
            </span>
          </li>
          {previo?.excluidosSinOptIn > 0 && (
            <li className="flex items-start gap-2">
              <ShieldCheck size={14} className="text-violet-500 flex-shrink-0 mt-0.5" />
              <span><strong>{num(previo.excluidosSinOptIn)}</strong> {plural(previo.excluidosSinOptIn, "queda excluido", "quedan excluidos")} por no haber dado permiso para recibir mensajes.</span>
            </li>
          )}
        </ul>
        <div className="flex gap-2 mt-5">
          <button onClick={onCerrar} className="flex-1 h-10 rounded-xl border border-slate-200 text-sm font-semibold text-slate-600 hover:bg-slate-50">Cancelar</button>
          {/* Sin líneas o fuera de la ventana horaria el botón se bloquea: no saldría ninguno. */}
          <button
            onClick={onConfirmar}
            disabled={!puedeConfirmar}
            className="flex-1 h-10 rounded-xl bg-emerald-600 text-white text-sm font-semibold hover:bg-emerald-700 disabled:opacity-40 inline-flex items-center justify-center gap-2"
          >
            <Send size={15} />
            {!previo?.encolados ? "Enviar"
              : previo.faltanLineas ? "Conectá una línea primero"
              : previo.enFranja === false ? "Fuera del horario de envío"
              : `Enviar ${num(previo.encolados)}`}
          </button>
        </div>
      </div>
    </div>
  );
}
